import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Section, Select } from '../components/form';
import { Button } from '../components/ui';
import { api } from '../lib/api';

interface DailyRow {
  day: string;
  total: number;
  answered: number;
  missed: number;
  billsec: number;
}

interface ExtensionRow {
  number: string;
  total: number;
  outbound: number;
  inbound: number;
  answered: number;
  missed: number;
  billsec: number;
  avg_talk: number;
}

interface HourRow {
  hour: number;
  total: number;
  answered: number;
}

interface QueueRow {
  id: string;
  name: string;
  number: string;
  total: number;
  answered: number;
  abandoned: number;
  avg_wait: number;
  max_wait: number;
  avg_talk: number;
  service_level: number;
}

interface AgentRow {
  number: string;
  name: string;
  handled: number;
  billsec: number;
  avg_talk: number;
  avg_wait: number;
}

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/** Aggregates over call records — the questions raw history cannot answer at a glance. */
export function Reports() {
  const [days, setDays] = useState('30');

  const { data: daily = [] } = useQuery({
    queryKey: ['report-daily', days],
    queryFn: () =>
      api.get<DailyRow[]>(`/api/v1/cdr/stats/summary?days=${Math.min(Number(days), 90)}`),
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['report-extensions', days],
    queryFn: () => api.get<ExtensionRow[]>(`/api/v1/reports/extensions?days=${days}`),
  });

  const { data: hourly = [] } = useQuery({
    queryKey: ['report-hourly', days],
    queryFn: () => api.get<HourRow[]>(`/api/v1/reports/hourly?days=${days}`),
  });

  const { data: queues = [] } = useQuery({
    queryKey: ['report-queues', days],
    queryFn: () => api.get<QueueRow[]>(`/api/v1/reports/queues?days=${days}`),
  });

  const { data: agents = [] } = useQuery({
    queryKey: ['report-agents', days],
    queryFn: () => api.get<AgentRow[]>(`/api/v1/reports/agents?days=${days}`),
  });

  const totals = daily.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      answered: acc.answered + row.answered,
      missed: acc.missed + row.missed,
      billsec: acc.billsec + row.billsec,
    }),
    { total: 0, answered: 0, missed: 0, billsec: 0 },
  );

  const answerRate = totals.total > 0 ? Math.round((totals.answered / totals.total) * 100) : 0;
  const busiest = hourly.reduce<HourRow | null>(
    (best, row) => (best === null || row.total > best.total ? row : best),
    null,
  );

  const columns: Array<Column<ExtensionRow>> = [
    {
      key: 'number',
      header: 'Extension',
      sort: (a, b) => a.number.localeCompare(b.number),
      render: (r) => <span className="font-mono">{r.number}</span>,
    },
    {
      key: 'total',
      header: 'Calls',
      sort: (a, b) => a.total - b.total,
      render: (r) => <span className="tabular-nums">{r.total}</span>,
    },
    {
      key: 'inbound',
      header: 'In',
      sort: (a, b) => a.inbound - b.inbound,
      render: (r) => <span className="tabular-nums">{r.inbound}</span>,
    },
    {
      key: 'outbound',
      header: 'Out',
      sort: (a, b) => a.outbound - b.outbound,
      render: (r) => <span className="tabular-nums">{r.outbound}</span>,
    },
    {
      key: 'missed',
      header: 'Missed',
      sort: (a, b) => a.missed - b.missed,
      render: (r) => (
        <span className={r.missed > 0 ? 'tabular-nums text-warning' : 'tabular-nums'}>
          {r.missed}
        </span>
      ),
    },
    {
      key: 'talk',
      header: 'Talk time',
      sort: (a, b) => a.billsec - b.billsec,
      render: (r) => <span className="tabular-nums">{hms(r.billsec)}</span>,
    },
    {
      key: 'avg',
      header: 'Average call',
      sort: (a, b) => a.avg_talk - b.avg_talk,
      render: (r) => <span className="tabular-nums">{hms(r.avg_talk)}</span>,
    },
  ];

  const peak = Math.max(1, ...hourly.map((h) => h.total));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Call reports"
        description="Volume, answer rate and busy hours, derived from call records."
        actions={
          <div className="flex items-center gap-2">
            <div className="w-40">
              <Select value={days} onChange={setDays}>
                {RANGES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </div>
            <a href={`/api/v1/cdr/export?days=${days}`} download>
              <Button variant="secondary">
                <Download size={14} />
                Export CSV
              </Button>
            </a>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label="Total calls" value={String(totals.total)} />
        <Stat label="Answered" value={`${answerRate}%`} />
        <Stat
          label="Missed"
          value={String(totals.missed)}
          tone={totals.missed > 0 ? 'warn' : undefined}
        />
        <Stat label="Talk time" value={hms(totals.billsec)} />
      </div>

      <Section
        title="Busiest hours"
        description={
          busiest
            ? `Peak is ${String(busiest.hour).padStart(2, '0')}:00 with ${busiest.total} calls.`
            : 'No calls in this period.'
        }
      >
        <div className="flex h-32 items-end gap-1">
          {Array.from({ length: 24 }, (_, hour) => {
            const row = hourly.find((h) => h.hour === hour);
            const total = row?.total ?? 0;
            return (
              <div key={hour} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t bg-primary/70"
                  style={{ height: `${(total / peak) * 100}%` }}
                  title={`${String(hour).padStart(2, '0')}:00 — ${total} call(s)`}
                />
                {hour % 3 === 0 && (
                  <span className="text-[10px] text-muted-foreground tabular-nums">{hour}</span>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <DataTable
        rows={extensions}
        columns={columns}
        rowKey={(r) => r.number}
        search={(r) => r.number}
        searchPlaceholder="Search extensions"
        empty="No calls in this period."
      />

      {queues.length > 0 && (
        <Section
          title="Queues"
          description="Service level is calls answered within 20 seconds, over everything that arrived — abandoned calls included."
        >
          <DataTable
            rows={queues}
            columns={QUEUE_COLUMNS}
            rowKey={(r) => r.id}
            search={(r) => `${r.name} ${r.number}`}
            searchPlaceholder="Search queues"
            empty="No queues."
          />
        </Section>
      )}

      {agents.length > 0 && (
        <Section
          title="Agents"
          description="Calls each person answered out of a queue, and how long callers waited to reach them."
        >
          <DataTable
            rows={agents}
            columns={AGENT_COLUMNS}
            rowKey={(r) => r.number}
            search={(r) => `${r.number} ${r.name}`}
            searchPlaceholder="Search agents"
            empty="Nobody has answered a queue call in this period."
          />
        </Section>
      )}
    </div>
  );
}

// Explicit `| undefined`: exactOptionalPropertyTypes rejects a conditionally-undefined prop.
function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' | undefined }) {
  return (
    <div className="rounded-[--radius] border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${tone === 'warn' ? 'text-warning' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Service level is coloured, because it is the one number here that is a target rather than an
 * observation — 80% is the long-standing contact-centre convention, and below it is the point at
 * which callers start noticing.
 */
const QUEUE_COLUMNS: Array<Column<QueueRow>> = [
  {
    key: 'name',
    header: 'Queue',
    sort: (a, b) => a.name.localeCompare(b.name),
    render: (r) => (
      <span>
        <span className="font-medium">{r.name}</span>
        <span className="ml-2 font-mono text-xs text-muted-foreground">{r.number}</span>
      </span>
    ),
  },
  {
    key: 'total',
    header: 'Calls',
    sort: (a, b) => a.total - b.total,
    render: (r) => <span className="tabular-nums">{r.total}</span>,
  },
  {
    key: 'answered',
    header: 'Answered',
    sort: (a, b) => a.answered - b.answered,
    render: (r) => <span className="tabular-nums">{r.answered}</span>,
  },
  {
    key: 'abandoned',
    header: 'Abandoned',
    sort: (a, b) => a.abandoned - b.abandoned,
    render: (r) => (
      <span className={r.abandoned > 0 ? 'tabular-nums text-warning' : 'tabular-nums'}>
        {r.abandoned}
      </span>
    ),
  },
  {
    key: 'service_level',
    header: 'Service level',
    sort: (a, b) => a.service_level - b.service_level,
    render: (r) => (
      <span
        className={
          r.total === 0
            ? 'tabular-nums text-muted-foreground'
            : r.service_level >= 80
              ? 'tabular-nums text-emerald-600 dark:text-emerald-500'
              : 'tabular-nums text-destructive'
        }
      >
        {r.total === 0 ? '—' : `${r.service_level}%`}
      </span>
    ),
  },
  {
    key: 'avg_wait',
    header: 'Avg wait',
    sort: (a, b) => a.avg_wait - b.avg_wait,
    render: (r) => <span className="tabular-nums">{hms(r.avg_wait)}</span>,
  },
  {
    key: 'max_wait',
    header: 'Longest wait',
    sort: (a, b) => a.max_wait - b.max_wait,
    render: (r) => <span className="tabular-nums">{hms(r.max_wait)}</span>,
  },
  {
    key: 'avg_talk',
    header: 'Avg talk',
    sort: (a, b) => a.avg_talk - b.avg_talk,
    render: (r) => <span className="tabular-nums">{hms(r.avg_talk)}</span>,
  },
];

const AGENT_COLUMNS: Array<Column<AgentRow>> = [
  {
    key: 'agent',
    header: 'Agent',
    sort: (a, b) => a.number.localeCompare(b.number),
    render: (r) => (
      <span>
        <span className="font-medium">{r.name}</span>
        <span className="ml-2 font-mono text-xs text-muted-foreground">{r.number}</span>
      </span>
    ),
  },
  {
    key: 'handled',
    header: 'Handled',
    sort: (a, b) => a.handled - b.handled,
    render: (r) => <span className="tabular-nums">{r.handled}</span>,
  },
  {
    key: 'billsec',
    header: 'Talk time',
    sort: (a, b) => a.billsec - b.billsec,
    render: (r) => <span className="tabular-nums">{hms(r.billsec)}</span>,
  },
  {
    key: 'avg_talk',
    header: 'Avg talk',
    sort: (a, b) => a.avg_talk - b.avg_talk,
    render: (r) => <span className="tabular-nums">{hms(r.avg_talk)}</span>,
  },
  {
    key: 'avg_wait',
    header: 'Avg wait before answer',
    sort: (a, b) => a.avg_wait - b.avg_wait,
    render: (r) => <span className="tabular-nums">{hms(r.avg_wait)}</span>,
  },
];
